/** @jsxImportSource @emotion/react */
import React, { memo } from 'react';
import { css } from '@emotion/react';
import { Box, Flex, Text, useTheme, CoreIcon } from '@vitvio/design-system';
import type { IconName } from '@vitvio/design-system';
import { useTranslation } from '@vitvio/locales';

export interface PatientConfirmationInfoCardProps {
  icon: IconName;
  labelKey: string;
  mainValue: string;
  bottomText?: string;
  badgeKey?: string;
  badgeValues?: Record<string, string>;
  secondaryLabelKey?: string;
  secondaryLabelValues?: Record<string, string>;
  highlighted?: boolean;
  fullWidth?: boolean;
  fixedWidth?: boolean;
}

export const PatientConfirmationInfoCard: React.FC<PatientConfirmationInfoCardProps> = memo(
  ({
    icon,
    labelKey,
    mainValue,
    bottomText,
    badgeKey,
    badgeValues,
    secondaryLabelKey,
    secondaryLabelValues,
    highlighted,
    fullWidth,
    fixedWidth,
  }) => {
    const { getSpacing, getColor, getRadius } = useTheme();
    const { t } = useTranslation();

    const label = t(labelKey);
    const badge = badgeKey ? t(badgeKey, badgeValues ?? {}) : undefined;
    const secondaryLabel = secondaryLabelKey ? t(secondaryLabelKey, secondaryLabelValues ?? {}) : undefined;

    return (
      <Box
        css={css`
          background: ${getColor('bg.sunken')};
          border-radius: ${getRadius('lg')};
          padding: ${getSpacing('6')};
          ${fullWidth ? 'width: 100%;' : ''}
          ${fixedWidth ? `width: ${getSpacing('60')}; flex-shrink: 0;` : 'flex: 1; min-width: 0;'}
          overflow: hidden;
        `}
      >
        <Box
          css={css`
            display: grid;
            grid-template-columns: auto ${getSpacing('2')} 1fr;
            column-gap: 0;
            row-gap: ${getSpacing('3')};
            align-items: center;
          `}
        >
          <Box
            css={css`
              margin-top: ${getSpacing('0.5')};
              margin-left: ${getSpacing('0.5')};
              grid-row: 1 / -1;
              align-self: start;
            `}
          >
            <CoreIcon icon={icon} size="md" color="fg.muted" />
          </Box>
          <Box />
          <Flex direction="column" gap={3} align="stretch">
            <Text font="L_Regular" colorToken="fg.muted" as="span">
              {label}
            </Text>
            <Flex align="flex-end" gap={3} wrap="nowrap">
              <Box
                css={css`
                  ${highlighted
                    ? `background: ${getColor('tag.warning.bg.default')}; padding: 0 ${getSpacing('1')}; border-radius: ${getRadius('sm')};`
                    : ''}
                  white-space: nowrap;
                `}
              >
                <Text font="headings.L_SemiBold" colorToken="fg">
                  {mainValue}
                </Text>
              </Box>
              {secondaryLabel && (
                <Text font="metric.M_Regular" colorToken="fg.muted" pb="1" whiteSpace="nowrap">
                  {secondaryLabel}
                </Text>
              )}
            </Flex>
            {(bottomText || badge) && (
              <Flex align="center" gap={3}>
                {bottomText && (
                  <Text font="L_Regular" colorToken="fg">
                    {bottomText}
                  </Text>
                )}
                {badge && (
                  <Box
                    css={css`
                      background: ${getColor('tag.primary.bg.default')};
                      padding: 0 ${getSpacing('1')};
                      border-radius: ${getRadius('sm')};
                    `}
                  >
                    <Text font="M_Regular" colorToken="fg">
                      {badge}
                    </Text>
                  </Box>
                )}
              </Flex>
            )}
          </Flex>
        </Box>
      </Box>
    );
  },
);

PatientConfirmationInfoCard.displayName = 'PatientConfirmationInfoCard';
